<script setup lang="ts">
/**
 * SeoSpreadExample — a worked example of a spread on its SEO page (pilot copy): the
 * question on a cream slip, the drawn cards as a row of stickers (each one a link to its
 * /karty page), then a pencil-ruled sheet with the reading per position, and the cat's
 * summary on a second slip. Pure props → static markup, so the text and every
 * <a href="/karty/…"> are in the pre-rendered HTML.
 */
import { RouterLink } from 'vue-router';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import SeoCardSticker from '@app/presentation/components/seo/SeoCardSticker.vue';

defineProps<{
  question: string;
  /** In draw order; `name` / `number` resolved from CARD_SEO_DATA by the page. */
  cards: Array<{ cardSlug: string; name: string; number: string; position: string; text: string }>;
  summary: string;
}>();
</script>

<template>
  <section class="pb-14 md:pb-20">
    <div class="site-container">
      <div class="mx-auto max-w-[860px]">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">
            Как это выглядит
          </div>
          <h2 class="type-title">Пример расклада</h2>
        </ScrollReveal>

        <ScrollReveal>
          <div
            class="seo-slip paper-tag px-6 py-5 text-paper-stock-ink md:px-8 md:py-6"
            style="--tilt: -0.4deg"
          >
            <div class="type-overline mb-2">
              Вопрос
            </div>
            <p class="type-body md:text-[17px]">{{ question }}</p>
          </div>
        </ScrollReveal>

        <!-- The spread as it was drawn: one sticker per card, in order. -->
        <ScrollReveal
          as="ul"
          class="mt-10 flex flex-wrap justify-center gap-x-5 gap-y-7 md:gap-x-7"
        >
          <li
            v-for="(card, i) in cards"
            :key="card.cardSlug"
            class="w-[84px] sm:w-[96px] md:w-[108px]"
          >
            <SeoCardSticker
              :slug="card.cardSlug"
              :name="card.name"
              :number="card.number"
              :index="i"
            />
          </li>
        </ScrollReveal>

        <ScrollReveal
          as="ol"
          class="seo-lines paper-sheet mt-10 px-5 md:px-7"
        >
          <li
            v-for="(card, i) in cards"
            :key="card.cardSlug"
            class="flex gap-4 py-5 md:py-6"
          >
            <span
              v-if="cards.length > 1"
              class="seo-disc type-label h-8 w-8"
              aria-hidden="true"
            >{{ i + 1 }}</span>
            <div class="min-w-0">
              <h3 class="type-app-heading text-foreground">
                {{ card.position }} —
                <RouterLink
                  :to="`/karty/${card.cardSlug}`"
                  class="seo-link"
                >{{ card.name }}</RouterLink>
              </h3>
              <p class="type-body mt-2 text-muted-foreground">{{ card.text }}</p>
            </div>
          </li>
        </ScrollReveal>

        <ScrollReveal class="mt-8">
          <div
            class="seo-slip paper-tag p-6 text-paper-stock-ink md:p-8"
            style="--tilt: 0.4deg"
          >
            <div class="type-overline mb-2">
              Итог
            </div>
            <p class="type-body md:text-[17px]">{{ summary }}</p>
          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
</template>

<style scoped src="./seo-paper.css"></style>
